import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { toast } from "react-hot-toast";

const BoostButton = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [boostActive, setBoostActive] = useState(false);
  const [boostExpiresAt, setBoostExpiresAt] = useState(null);
  const [checkingExpiry, setCheckingExpiry] = useState(true);

  // 🔥 Real-time listener for boost status
  useEffect(() => {
    if (!userId) return;

    const unsub = onSnapshot(doc(db, "users", userId), async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const active = data.boostActive || false;
        const exp = data.boostExpiresAt?.toDate?.() || (data.boostExpiresAt ? new Date(data.boostExpiresAt) : null);
        setBoostActive(active);
        setBoostExpiresAt(exp);

        // If boost appears expired but flags still set, clear them via callable
        const now = new Date();
        const isExpired = exp ? now >= exp : !!(data.boostActive || data.boostedUntil || data.boostedAt);
        if (isExpired && (data.boostActive || data.boostExpiresAt || data.boostedUntil || data.boostedAt)) {
          try {
            const clearFn = httpsCallable(functions, "clearExpiredBoost");
            await clearFn({ userId });
          } catch (e) {
            console.warn("clearExpiredBoost failed:", e?.message || e);
          }
        }
      }
      setCheckingExpiry(false);
    });

    return () => unsub();
  }, [userId]);

  const handleBoost = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const yoco = new window.YocoSDK({
        publicKey: process.env.REACT_APP_YOCO_PUBLIC_KEY,
      });

      const result = await yoco.showPopup({
        amountInCents: 199900, // R1999.00 for 7-day boost
        currency: "ZAR",
        name: "InvestorLanes Boost",
        description: "7-day premium visibility",
      });

      console.log("👉 Yoco popup result:", result);

      // Popup closed without token → exit silently
      if (!result?.id) {
        console.warn("⚠️ No payment token generated (popup closed or incomplete)");
        setLoading(false);
        return;
      }

      toast.loading("⏳ Processing payment, please wait...");

      const createPayment = httpsCallable(functions, "createYocoPaymentV2");
      const response = await createPayment({
        token: result.id,
        planId: "boost7",
        userId,
      });

      toast.dismiss();

      // Handle backend response
      if (response.data.success) {
        toast.success("🔥 Your boost is active for 7 days!");
      } else if (response.data.status === "pending") {
        toast("⏳ Payment pending 3DS verification, we’ll update you once confirmed.");
      } else {
        toast.error(response.data.error || "⚠️ Payment failed. Please try again.");
      }

    } catch (err) {
      toast.dismiss();
      console.error("Boost Error:", err);
      toast.error(err.message || "⚠️ Something went wrong during payment.");
    } finally {
      setLoading(false);
    }
  };

  // Show expiration if boost already active
  if (boostActive && boostExpiresAt) {
    return (
      <div className="text-center mt-4">
        <p className="text-green-600 font-semibold">
          🔥 Boost Active until {boostExpiresAt.toLocaleDateString()} 🎉
        </p>
        <button
          disabled
          className="px-4 py-2 mt-2 rounded-full bg-gray-400 text-white font-semibold cursor-not-allowed"
        >
          Boost Already Active
        </button>
      </div>
    );
  }

  // Optional: show a button to fix stale state if UI still thinks expired
  if (!boostActive && boostExpiresAt && new Date() >= boostExpiresAt && !checkingExpiry) {
    return (
      <div className="text-center mt-4">
        <p className="text-yellow-700 text-sm">Your previous boost expired on {boostExpiresAt.toLocaleDateString()}.</p>
        <button
          onClick={async () => {
            try {
              const clearFn = httpsCallable(functions, "clearExpiredBoost");
              await clearFn({ userId, force: true });
              toast.success("Expired boost cleared. You can boost again now.");
            } catch (e) {
              toast.error(e?.message || "Could not clear expired boost");
            }
          }}
          className="px-4 py-2 mt-2 rounded-full bg-gray-700 hover:bg-gray-800 text-white font-semibold"
        >
          Clear expired boost
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleBoost}
      disabled={loading}
      className={`px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold transition mt-4 ${
        loading ? "cursor-not-allowed opacity-70" : ""
      }`}
    >
      {loading ? "Processing..." : "🔥 Boost Now – 7 Days"}
    </button>
  );
};

export default BoostButton;
